<script setup lang="ts">
import type { DispatchDefectLogisticsTask } from '~/server/trpc/routers/logisticsPlanning'

const props = defineProps<{
  task: DispatchDefectLogisticsTask
  isClicked: Ref<boolean>
  showActionButtons?: boolean
}>()
const emit = defineEmits<{
  (e: 'click'): void
  (e: 'update'): void
  (e: 'delete'): void
  (e: 'updateDeliveryAt', newDeliveryAt: Date): void
}>()

const { openLogisticsTaskInformationPopup } = useGlobalOpeners()

const formattedDeliveryTime = computed(() => props.task.deliveryAt ? useDateAsString(props.task.deliveryAt, 'HH:mm') : null)
const selectedDeliveryTime = ref(props.task.deliveryAt?.getTime() ?? null)
const fieldsToShow = computed((): {
  customer: string
  machineryId: string | null
  machineryAccessoryId: string | null
  deliveryFrom: string
  deliveryTo: string
} => {
  return {
    customer: props.task.customer?.name ?? 'N/A',
    machineryId: props.task.defect?.machineryId ?? null,
    machineryAccessoryId: props.task.defect?.machineryAccessoryId ?? null,
    deliveryFrom: props.task.deliveryFrom ?? '',
    deliveryTo: props.task.deliveryTo ?? '',
  }
})
const clickedClass = computed(() => {
  if (props.isClicked.value && !props.task.isCancelled) {
    return 'ClickedTask'
  }

  return null
})

const updateDeliveryDate = useDebounceFn((v: number | null) => {
  if (!v || v === props.task.deliveryAt?.getTime()) {
    return
  }

  emit('updateDeliveryAt', new Date(v))
})

const formattedPlannedDeliveryAt = computed(() => {
  return props.task.plannedDeliveryAt ? useDateAsString(props.task.plannedDeliveryAt, 'dd.MM.yyyy') : null
})
</script>

<template>
  <n-collapse
    :class="[clickedClass]"
    class="Defect p-2 border rounded-md flex min-w-[356px]"
  >
    <n-collapse-item class="w-full">
      <template #header>
        <div class="flex w-full" @click.stop="$emit('click')">
          <n-popover trigger="hover">
            <template #trigger>
              <div class="w-6" @click.stop="openLogisticsTaskInformationPopup.open({ id: props.task.id })">
                <Icon name="material-symbols:info" />
              </div>
            </template>

            <div class="space-y-4">
              <LogisticsStatusTimeline v-if="task" :logistics-task-id="task.id" horizontal />
              <p>
                <span class="font-semibold">{{ $t('logisticsTask.createOrEdit.plannedDeliveryAt.label') }}:</span> {{ formattedPlannedDeliveryAt }}
              </p>
            </div>
          </n-popover>

          <div
            class="grow flex flex-col gap-y-1"
          >
            <div v-for="[label, value] in Object.entries(fieldsToShow)" :key="label" class="space-x-2">
              <template v-if="value !== null">
                <span class="font-semibold">{{ $t(`logisticsPlanning.defectTaskBreadcrumb.field.${label}`) }}:</span>
                <n-ellipsis style="max-width: 180px; ">
                  {{ value }}
                </n-ellipsis>
              </template>
            </div>
          </div>

          <div class="justify-self-end flex flex-col gap-y-1 items-center">
            <div v-if="showActionButtons" class="flex gap-x-2">
              <n-button class="Button" @click.stop="$emit('update')">
                <Icon name="material-symbols:edit-outline-rounded" class="text-2xl" />
              </n-button>
              <n-button class="Button" @click.stop="$emit('delete')">
                <Icon name="material-symbols:delete-outline-rounded" class="text-2xl" />
              </n-button>
            </div>
            <div v-else>
              <n-popover
                trigger="hover"
                @update:show="selectedDeliveryTime = task.deliveryAt?.getTime() ?? null"
              >
                <template #trigger>
                  <p class="underline w-[40px]">
                    {{ formattedDeliveryTime }}
                  </p>
                </template>
                <n-time-picker
                  :value="selectedDeliveryTime"
                  format="HH:mm"
                  size="small"
                  time-zone="Europe/Berlin"
                  :minutes="15"
                  @update:value="v => selectedDeliveryTime = v"
                  @confirm="updateDeliveryDate"
                />
              </n-popover>
            </div>
          </div>
        </div>
      </template>
      <div class="rounded bg-gray-100 border-gray-200 border p-2">
        {{ task.remarks }}
      </div>
    </n-collapse-item>
  </n-collapse>
</template>

<style scoped>
.Defect {
  @apply bg-purple-200
}

.Button {
  @apply text-lg cursor-pointer
}

.ClickedTask {
  @apply bg-white animate-pulse
}
</style>
